import React, { useContext } from 'react';
import classNames from 'classnames';
import { FieldArray } from 'react-final-form-arrays';
import { Field, useField } from 'react-final-form';
import DatepickerField from 'components/DatepickerField';
import InputField from 'components/InputField';
import SelectField from 'components/SelectField';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import { Button } from 'ncoded-component-library';
import PluscircleIcon from 'icons/Pluscircle.icon';
import { useTranslation } from 'react-i18next';
import { dateValidator, onlyFuture } from 'validations/general';
import validations from 'validations';
import useMatchMedia from 'hooks/useMatchMedia';
import { ListingDeadline } from 'models/Listing';
import { timeOptions, DEFAULT_TIME_OPTION } from 'data/timeOptions';
import PatchListingContext from 'router/subrouters/ListingCreationWizard/providers/PatchListing/PatchListing.context';

import './DeadlineArrayField.styles.scss';
import './DeadlineArrayField.styles.responsive.scss';

type DeadlineArrayFieldProps = {
  className?: string;
};

const DeadlineArrayField: React.FC<DeadlineArrayFieldProps> = (props) => {
  const { className } = props;

  const { t } = useTranslation();
  const classes = classNames('deadline-array-field', className);

  const {
    input: { value: deadline },
  } = useField<ListingDeadline[]>('deadline');

  const { patchListingValue } = useContext(PatchListingContext);

  const isBellowMobile = useMatchMedia('isBelowMobile');

  return (
    <div className={classes}>
      <FieldArray<ListingDeadline> name="deadline">
        {({ fields }) => {
          return (
            <>
              {fields.map((name, index) => {
                const el = deadline[index];

                return (
                  <div
                    key={name}
                    className={classNames('deadline-array-field__deadline', {
                      'deadline-array-field__deadline--no-fixed-date':
                        !!el?.noFixedDate,
                    })}
                  >
                    <div className="deadline-array-field__fields">
                      <Field
                        name={`${name}.timeline`}
                        label={t('deadlineMilestone')}
                        placeholder={t('enterEventName')}
                        validate={validations.required()}
                        component={InputField}
                        readOnly={index === 0}
                        customOnBlur={(
                          e: React.FocusEvent<HTMLTextAreaElement>,
                          isValid: boolean,
                        ) => {
                          const newValues = [...deadline];
                          newValues[index].timeline = e.target.value;

                          if (isValid) {
                            patchListingValue({
                              deadline: newValues,
                            });
                          }
                        }}
                      />
                      <Field
                        name={`${name}.timelineDate`}
                        label={t('date')}
                        disabled={deadline[index]?.noFixedDate}
                        onlyFuture
                        component={DatepickerField}
                        validate={validations.composeValidators(
                          dateValidator(index, 'noFixedDate'),
                          onlyFuture(t('onlyFuture')),
                        )}
                        customOnChange={(date: string, isValid: boolean) => {
                          const newValues = [...deadline];
                          newValues[index].timelineDate = date;

                          if (isValid) {
                            patchListingValue({
                              deadline: newValues,
                            });
                          }
                        }}
                      />
                      <Field
                        name={`${name}.timelineTime`}
                        label={t('time')}
                        disabled={deadline[index]?.noFixedDate}
                        validate={validations.required()}
                        component={SelectField}
                        options={timeOptions}
                        defaultValue={DEFAULT_TIME_OPTION}
                        customOnChange={(value: string, isValid: boolean) => {
                          const newValues = [...deadline];
                          newValues[index].timelineTime = value;

                          if (isValid && el?.timelineDate) {
                            patchListingValue({
                              deadline: newValues,
                            });
                          }
                        }}
                      />
                    </div>
                    {index > 0 && (
                      <div
                        className={classNames('deadline-array-field__actions', {
                          'deadline-array-field__actions--first': index === 0,
                        })}
                      >
                        <Field
                          name={`${name}.noFixedDate`}
                          label={t('noFixedDate')}
                          type="checkbox"
                          component={CheckboxField}
                          customOnChange={(noFixedDate: boolean) => {
                            const newValues = [...deadline];
                            newValues[index].noFixedDate = noFixedDate;

                            if (noFixedDate) {
                              newValues[index].timelineDate = null;
                            }

                            patchListingValue({
                              deadline: newValues,
                            });
                          }}
                        />
                        <Button
                          type="button"
                          variant="link"
                          disabled={index === 0}
                          onClick={() => {
                            if (index !== 0) {
                              fields.remove(index);

                              const newFields = [...deadline];
                              newFields.splice(index, 1);

                              patchListingValue({
                                deadline: newFields,
                              });
                            }
                          }}
                        >
                          {t('removeEvent')}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
              <Button
                variant="link"
                onClick={() => {
                  fields.push({
                    timelineDate: '',
                    timeline: '',
                    noFixedDate: false,
                  });
                }}
              >
                <PluscircleIcon />
                {t(`addAnotherDeadline${isBellowMobile ? '' : 'Date'}`)}
              </Button>
            </>
          );
        }}
      </FieldArray>
    </div>
  );
};

export default DeadlineArrayField;
